/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import { ResponsiveBar } from "@nivo/bar";
import React, { useEffect, useState } from "react";
import { months } from "../../components/DateRangePicker";
import fetchHelper from "../../services/fetchHelper";
import { queryDateFormat } from "../../stats/cst";
import colors from "./colors.json";
import useError from "../../hooks/useError";
import initialData from "./initialData.json";

const Text = ({ children }) => {
  const [id, setId] = useState(Math.random().toString(36).substring(7));
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const elem = document.getElementById(id);
    const width = elem.getBBox().width;
    setWidth(width);
  }, []);
  return (
    <text
      id={id}
      x={0 - width}
      fontWeight="bold"
      textAnchor="left"
      dominantBaseline="middle"
      style={{
        fill: "#333",
        fontSize: 10,
      }}
    >
      {children}
    </text>
  );
};

const Graph = ({ filter, isLoading = () => null, locationFilter }) => {
  const [jsonData, setJsonData] = useState(initialData);
  const [data, setData] = useState([]);
  const [keys, setKeys] = useState([]);
  const [totalSum, setTotalSum] = useState({});
  const [legendWidth, setLegendWidth] = useState(0);
  const [locationNames, setLocationsName] = useState({});
  const [totalM30M90Arr, settotalM30M90Arr] = useState([]);
  const showDefaultApiError = useError();

  useEffect(() => {
    const elem = document.getElementById("keys-legends");
    if (elem) {
      setLegendWidth(elem.offsetWidth);
    }
  }, [locationNames]);

  const fetchData = async ({ urlQuery }) => {
    isLoading(true);
    try {
      let url = "/patientmanagement/rest/interrogations/billableLocationReport?";
      const data = await fetchHelper.get({ url: url + urlQuery });
      setJsonData(data);
    } catch (err) {
      showDefaultApiError(err);
      console.log(err);
    } finally {
      isLoading(false);
    }
  };

  useEffect(() => {
    if (filter) {
      if (filter.startDate) filter.fromDate = queryDateFormat(filter.startDate);
      if (filter.endDate) filter.toDate = queryDateFormat(filter.endDate);
      delete filter.startDate;
      delete filter.endDate;
      delete filter.temp;
      const urlQuery = new URLSearchParams(filter);
      fetchData({ urlQuery });
    }
  }, [filter]);

  useEffect(() => {
    const keys = [];
    const ln = { ...locationNames };
    const a = (jsonData || []).sort((a, b) => {
      const aMonth = parseInt(a.month.replace("-", ""));
      const bMonth = parseInt(b.month.replace("-", ""));
      return aMonth - bMonth;
    });
    let totalSum = {};

    const legendSorting = [];
    const data = a.map((item) => {
      let totalm30 = 0;
      let totalm90 = 0;
      let totalm30L = 0;
      let totalm90L = 0;
      const d = {
        month: item.month,
      };
      const items = item.billableLocations || [];
      items.sort((a, b) => a.billable.m30 - b.billable.m30);
      let totalM30M90Arr = [];
      let dd = {};
      items.forEach((d2, index) => {
        const filterData = locationFilter[d2.locationId] || {};
        const fm30 = parseInt(filterData.m30 || 0);
        const fm90 = parseInt(filterData.m90 || 0);
        totalm30 += fm30 * d2.billable.m30;
        totalm90 += fm90 * d2.billable.m90;
        dd[d2.name] = dd[d2.name] || { m30: 0, m90: 0 };
        dd[d2.name].m30 += d2.billable.m30;
        dd[d2.name].m90 += d2.billable.m90;
        dd[d2.name].color = colors[index % colors.length];
        dd[d2.name].ac_count = item.acCount;
        totalm30L += d2.billable.m30;
        totalm90L += d2.billable.m90;
        const key = d2.name + " m30";
        const key2 = d2.name + " m90";
        ln[key] = d2.name;
        ln["ac_count" + key + item.month] = item.acCount;
        ln["ac_count" + key2 + item.month] = item.acCount;
        ln[key2] = d2.name;
        d[key] = d2.billable.m30;
        d[key2] = d2.billable.m90 * -1;
        d[key + "Color"] = colors[index % colors.length];
        d[key2 + "Color"] = colors[index % colors.length];
        if (totalM30M90Arr.map((i) => i.name).indexOf(d2.name) < 0)
          totalM30M90Arr.push({ name: d2.name, color: colors[index % colors.length] });
        if (keys.indexOf(key) < 0) {
          keys.push(key);
        }

        if (keys.indexOf(key2) < 0) {
          keys.push(key2);
        }
      });
      for (let key in dd) {
        legendSorting.push({ key: key, value: dd[key].m30, color: dd[key].color });
        legendSorting.push({ key: key, value: dd[key].m90, color: dd[key].color });
      }
      totalSum = { ...totalSum, [item.month]: { totalm30, totalm90, totalm30L, totalm90L } };
      return d;
    });
    legendSorting.sort((a, b) => a.value - b.value);
    legendSorting.reverse();
    const dd = [];
    legendSorting.forEach((e) => {
      if (!dd.map((i) => i.name).includes(e.key)) {
        dd.push({ name: e.key, value: e.value, color: e.color });
      }
    });
    settotalM30M90Arr(dd);
    setTotalSum(totalSum);

    setLocationsName(ln);
    setKeys(keys);
    setData(data);
  }, [jsonData, locationFilter]);

  const nFormatter = (n) => {
    if (n >= 1e9) return +(n / 1e9).toFixed(1) + "g";
    else if (n >= 1e6) return +(n / 1e6).toFixed(1) + "m";
    else if (n >= 1e3) return +(n / 1e3).toFixed(0) + "k";
    else return n;
  };

  const CustomTick = (tick) => {
    const splitValue = tick.value.split("-");
    return (
      <g transform={`translate(${tick.x},${tick.y + 22})`}>
        <Text>{months[parseInt(splitValue[1]) - 1]}</Text>
        <text
          x={2}
          textAnchor="left"
          dominantBaseline="middle"
          style={{
            fill: "#333",
            fontSize: 10,
          }}
        >
          {splitValue[0].slice(2)}
        </text>
        <text
          y={15}
          textAnchor="middle"
          x={0}
          dominantBaseline="middle"
          style={{ fill: "#EE5D74", fontSize: 10 }}
        >
          (30)
        </text>
        <text
          fontWeight="bold"
          y={30}
          x={0}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fill: "#EE5D74", fontSize: 10 }}
        >
          {`$${nFormatter((totalSum[tick.value] || {}).totalm30 || 0)}`}
        </text>
        <text
          fontWeight="bold"
          y={45}
          x={0}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fill: "#EE5D74", fontSize: 10 }}
        >
          {(totalSum[tick.value] || {}).totalm30L || 0}
        </text>
        <text
          y={60}
          textAnchor="middle"
          x={0}
          dominantBaseline="middle"
          style={{ fill: "#008c98", fontSize: 10 }}
        >
          (90)
        </text>
        <text
          fontWeight="bold"
          y={75}
          x={0}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fill: "#008c98", fontSize: 10 }}
        >
          {`$${nFormatter((totalSum[tick.value] || {}).totalm90 || 0)}`}
        </text>
        <text
          fontWeight="bold"
          y={90}
          x={0}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fill: "#008c98", fontSize: 10 }}
        >
          {(totalSum[tick.value] || {}).totalm90L || 0}
        </text>
      </g>
    );
  };

  const isZero = () => {
    let i = 0;
    for (let key in totalSum) {
      i += (totalSum[key] || {}).totalm30L || 0;
      i += (totalSum[key] || {}).totalm90L || 0;
    }
    return i === 0;
  };

  return (
    <div style={{ height: 800, width: "100%", display: "flex" }}>
      <div
        style={{
          display: "flex",
          flex: 1,
          position: "relative",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 20 }}>Billable Events Count / Blended Rate Modeler</span>
        <ResponsiveBar
          data={data}
          keys={keys}
          minValue={isZero() ? 0 : undefined}
          maxValue={isZero() ? 0 : undefined}
          indexBy="month"
          margin={{ top: 50, right: legendWidth + 21, bottom: 140, left: 60 }}
          padding={0.3}
          colors={(data) => {
            return data.data[data.id + "Color"];
          }}
          label={(data) => {
            const name = locationNames[data.id].split("-")[0];
            if (name.length <= 5) return name;
            else return name.slice(0, 4) + "..";
            // return locationNames[data.id].split('-')[0]
          }}
          borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 50,
            tickRotation: 0,
            legend: "",
            legendPosition: "middle",
            legendOffset: 32,
            renderTick: CustomTick,
          }}
          axisTop={{
            tickPadding: 5,
            tickRotation: 0,
            legendPosition: "middle",
            legendOffset: -32,
            format: () => null,
            tickSize: 0,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "",
            legendPosition: "middle",
            legendOffset: -40,
            format: (value) => (value + "").replace("-", ""),
          }}
          markers={[
            {
              axis: "y",
              value: 0,
              lineStyle: { strokeOpacity: 0.1, stroke: "#fff", strokeWidth: 10 },
              textStyle: { fill: "#008c98" },
              legend: "30 Day Billable",
              legendPosition: "top-left",
              legendOrientation: "vertical",
              legendOffsetY: 30,
              legendOffsetX: -50,
            },
            {
              axis: "y",
              value: 0,
              lineStyle: { stroke: "#fff", strokeWidth: 6 },
              textStyle: { fill: "#EE5D74" },
              legend: "90 Day Billable",
              legendPosition: "bottom-left",
              legendOrientation: "vertical",
              legendOffsetY: 30,
              legendOffsetX: -50,
            },
          ]}
          tooltipFormat={(value) => (value + "").replace("-", "")}
          tooltip={(item) => {
            return (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>
                  {item.id}: {(item.value + "").replace("-", "")}
                </span>
                {locationNames["ac_count" + item.id + item.indexValue] ? (
                  <span>AC Count: {locationNames["ac_count" + item.id + item.indexValue]}</span>
                ) : null}
              </div>
            );
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          animate={true}
          motionStiffness={90}
          motionDamping={15}
        />
        <div
          style={{
            position: "absolute",
            bottom: 30,
            right: -10,
            flexDirection: "row",
            display: "flex",
            height: "100%",
            alignItems: "center",
          }}
          id="keys-legends"
        >
          <div>
            {totalM30M90Arr.slice(0, 25).map((t, index) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    paddingRight: 5,
                    marginBottom: 3,
                  }}
                  key={`key-legend-2-container-${index}`}
                >
                  <div style={{ width: 10, height: 10, backgroundColor: t.color }} />
                  <div style={{ fontSize: 12, padding: "0 5px" }}>{t.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Graph;
